import type { MonthlyData, Zone } from 'types/match'

import { useEffect } from 'react'
import styled from 'styled-components'

import { clamp } from 'utils'
import copy from 'copy.json'

import Badge from 'pages/matches//Badge'

const texts = copy.page.matches.graph

type Props = {
  hoveredItem: number
  entry: MonthlyData
  onShowZones: (zones: Zone[]) => void
}

export default function Tooltip({ hoveredItem, entry, onShowZones }: Props) {
  const moveTooltip = (hoveredItem: number) => {
    const tooltip = document.querySelector('.tooltip') as HTMLElement
    const barsWrapper = document.querySelector('.bars') as HTMLElement
    const arrow = document.querySelector('.arrow') as HTMLElement
    const bars = document.querySelectorAll('.bar') as NodeListOf<HTMLElement>
    const bar = bars[hoveredItem]

    if (!tooltip || !barsWrapper || !bar) return

    const margin = 24
    const offset = 16

    const barRect = bar.getBoundingClientRect()
    const barsWrapperRect = barsWrapper.getBoundingClientRect()
    const tooltipRect = barsWrapper.getBoundingClientRect()

    const leftEdge = barRect.x + bar.offsetWidth / 2 - tooltip.offsetWidth / 2
    const topEdge = barsWrapperRect.y - tooltip.offsetHeight - offset

    const tooltipLeft = clamp(margin, leftEdge, window.innerWidth - tooltip.offsetWidth - margin)
    const tooltipTop = clamp(offset, topEdge, window.innerHeight - tooltip.offsetHeight - offset)
    const arrowLeft = clamp(tooltipRect.x, barRect.x, tooltipRect.x + tooltipRect.width - margin)
    const arrowTop = tooltipTop + tooltip.offsetHeight

    tooltip.style.left = `${tooltipLeft}px`
    tooltip.style.top = `${tooltipTop}px`
    arrow.style.left = `${arrowLeft}px`
    arrow.style.top = `${arrowTop}px`
  }

  useEffect(() => moveTooltip(hoveredItem), [hoveredItem])

  return (
    <StyledTooltip className="tooltip dark">
      <Row>
        <Column>
          <span>{texts.total_coverage}:</span>
          <Badge coverage>
            {entry.total_coverage} {copy.common.MWh}
          </Badge>
          <Badge border>{Math.round(entry.coverage_percentage)}%</Badge>
        </Column>

        <Column>
          <span>{texts.total_shortage}:</span>
          <Badge shortage>
            {entry.total_shortage} {copy.common.MWh}
          </Badge>
          <Badge border>{Math.round(entry.shortage_percentage)}%</Badge>
        </Column>

        <Column>
          <span>{texts.total_surplus}:</span>
          <Badge surplus>
            {entry.total_surplus} {copy.common.MWh}
          </Badge>
          <Badge border>{Math.round(entry.surplus_percentage)}%</Badge>
        </Column>
      </Row>

      <button className="green text" onClick={() => onShowZones(Object.values(entry.zones_data))}>
        {texts.show_zones}
      </button>

      <Arrow className="arrow" />
    </StyledTooltip>
  )
}

const StyledTooltip = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  max-width: calc(100% - 48px);
  padding: 16px;
  background: var(--dark-gray);
  border-radius: 4px;
  white-space: nowrap;
  position: fixed;
  z-index: 1;
  transition: left 0.2s;
`

const Arrow = styled.span`
  width: 24px;
  height: 24px;
  background: inherit;
  border-radius: 2px;
  position: fixed;
  z-index: -1;
  transform: translateY(-18px) rotate(45deg);
  transition: left 0.2s;
`

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;

  > span:not(${Badge}) {
    font: var(--600-12);
  }
`
